import React, { useState, useEffect, useCallback } from 'react';
import { adminCategories } from '../../utils/api';
import toast from 'react-hot-toast';

const emptyCategory = {
  name: '', name_ar: '', slug: '', description: '', description_ar: '',
  image_url: '', sort_order: 0, is_active: 1,
};

const autoSlug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const AdminCategories = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCategory);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminCategories.getAll()
      .then(setCats)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyCategory); setEditing(null); setShowForm(true); };
  const openEdit = (c) => { setForm({ ...emptyCategory, ...c }); setEditing(c.id); setShowForm(true); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await adminCategories.update(editing, form);
        toast.success('Category updated');
      } else {
        await adminCategories.create(form);
        toast.success('Category created');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Deactivate category "${c.name}"?`)) return;
    try {
      await adminCategories.delete(c.id);
      toast.success('Category deactivated');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <div className="admin-page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h2 style={{ fontFamily:'var(--font-serif)', fontSize:'1.5rem' }}>
          Categories <span style={{ fontSize:'1rem', color:'var(--text-muted)', fontFamily:'var(--font-sans)' }}>({cats.length})</span>
        </h2>
        <button className="btn btn-primary" onClick={openNew}>+ Add Category</button>
      </div>

      <div className="admin-card">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Products</th>
                <th>Sort</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>
              ) : cats.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>No categories yet. Click "Add Category" to create one.</td></tr>
              ) : cats.map(c => (
                <tr key={c.id}>
                  <td>
                    <div>
                      <strong style={{ fontSize:'0.9rem' }}>{c.name}</strong>
                      {c.name_ar && <p style={{ fontSize:'0.75rem', color:'var(--text-muted)', direction:'rtl' }}>{c.name_ar}</p>}
                    </div>
                  </td>
                  <td style={{ fontSize:'0.78rem', fontFamily:'monospace', color:'var(--text-muted)' }}>{c.slug}</td>
                  <td><strong>{c.product_count || 0}</strong></td>
                  <td>{c.sort_order || 0}</td>
                  <td>
                    {c.is_active
                      ? <span className="badge badge-olive">Active</span>
                      : <span className="badge badge-red">Inactive</span>}
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="action-btn" onClick={() => openEdit(c)}>Edit</button>
                      {c.is_active ? <button className="action-btn action-btn--danger" onClick={() => handleDelete(c)}>Deactivate</button> : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal modal--lg">
            <div className="modal-header">
              <h3>{editing ? 'Edit Category' : 'New Category'}</h3>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Name (EN) *</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={e => { handleChange(e); if (!editing) setForm(f => ({ ...f, slug: autoSlug(e.target.value) })); }}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Name (AR)</label>
                  <input name="name_ar" value={form.name_ar || ''} onChange={handleChange} className="form-control" dir="rtl" />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">URL Slug *</label>
                  <input name="slug" value={form.slug} onChange={handleChange} className="form-control" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input name="sort_order" type="number" value={form.sort_order ?? 0} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Image URL</label>
                <input name="image_url" value={form.image_url || ''} onChange={handleChange} className="form-control" placeholder="https://..." />
              </div>

              <div className="form-group">
                <label className="form-label">Description (EN)</label>
                <textarea name="description" value={form.description || ''} onChange={handleChange} className="form-control" rows="3" />
              </div>

              <div className="form-group">
                <label className="form-label">Description (AR)</label>
                <textarea name="description_ar" value={form.description_ar || ''} onChange={handleChange} className="form-control" rows="3" dir="rtl" />
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={handleChange} />
                  Active
                </label>
              </div>

              <div style={{ display:'flex', gap:12, marginTop:20 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Update Category' : 'Create Category')}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .admin-card { background: white; border-radius: var(--radius-lg); padding: 20px; box-shadow: var(--shadow-sm); }
        .admin-table-wrap { overflow-x: auto; }
        .admin-table { width: 100%; border-collapse: collapse; }
        .admin-table th { text-align: left; padding: 10px 12px; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); border-bottom: 2px solid var(--border); }
        .admin-table td { padding: 12px; font-size: 0.85rem; border-bottom: 1px solid #f5f5f5; vertical-align: middle; }
        .admin-table tr:hover td { background: #fafafa; }
        .action-btn { padding: 6px 14px; background: var(--cream); border-radius: 6px; font-size: 0.78rem; font-weight: 600; font-family: var(--font-sans); transition: all 0.2s; }
        .action-btn:hover { background: var(--gold); color: white; }
        .action-btn--danger:hover { background: #e53e3e; color: white; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .modal { background: white; border-radius: var(--radius-lg); width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; }
        .modal--lg { max-width: 680px; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--border); position: sticky; top: 0; background: white; z-index: 1; }
        .modal-header h3 { font-family: var(--font-serif); }
        .modal-body { padding: 24px; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .form-checkboxes { display: flex; gap: 20px; flex-wrap: wrap; }
        .checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; font-weight: 500; cursor: pointer; }
        @media (max-width: 480px) { .form-grid-2 { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default AdminCategories;
